// filepath: cryptobag-com/src/pages/Prices.jsx
import React, { useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import { useApi } from '../hooks/useApi';
import { icons } from '../assets/icons';

// Import Components
import GlobalMarketTicker from '../components/GlobalMarketTicker';
import MarketPulse from '../components/MarketPulse';
import FearGreedGauge from '../components/FearGreedGauge';
import TrendingNow from '../components/TrendingNow';
import CryptoTable from '../components/CryptoTable';
import CryptoTableSkeleton from '../components/CryptoTableSkeleton';

const Prices = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentQuery = searchParams.get('q') || '';
  const searchTerm = currentQuery;
  const navigate = useNavigate();

  // Route-based click handler
  const handleCoinClick = (coinId) => {
    navigate(`/coin/${coinId}`);
  };

  // Request 100 coins at once - no pagination
  const coinsApiUrl = `/api/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=100&sparkline=true&price_change_percentage=24h,7d`;

  // Use the lazy-loading API hook
  const { data: coinsData, loading: coinsLoading, error: coinsError, fetchData: fetchCoins } = useApi(coinsApiUrl, true);

  // Fetch coins once on mount
  useEffect(() => {
    fetchCoins(coinsApiUrl);
  }, [coinsApiUrl, fetchCoins]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    const nextParams = new URLSearchParams(searchParams);
    if (value) {
      nextParams.set('q', value);
    } else {
      nextParams.delete('q');
    }

    setSearchParams(nextParams, { replace: true });
  };

  // Fetch Fear & Greed Index
  const { data: sentimentData, loading: sentimentLoading, error: sentimentError } = useApi('/api/sentiment');
  
  // Fetch Trending Coins
  const { data: trendingData, loading: trendingLoading, error: trendingError } = useApi('/api/trending');

  const fearGreed = sentimentData?.fear_greed || null;
  const trendingCoins = trendingData?.coins || [];

  const filteredCoins = useMemo(() => {
    const coins = coinsData || [];
    const query = searchTerm.trim().toLowerCase();
    return coins.filter((coin) =>
      coin.name.toLowerCase().includes(query) ||
      coin.symbol.toLowerCase().includes(query)
    );
  }, [coinsData, searchTerm]);

  return (
    <>
      <Helmet>
        <title>{currentQuery ? `Search "${currentQuery}" in Live Crypto Prices | CryptoBag` : 'Cryptocurrency Prices by Market Cap | CryptoBag'}</title>
        <meta
          name="description"
          content={
            currentQuery
              ? `Search CryptoBag's live market table for "${currentQuery}" across the current top market-cap cryptocurrencies.`
              : 'Browse live prices for top cryptocurrencies, sorted by market capitalization. 24-hour change, volume, and supply data.'
          }
        />
        {currentQuery && <meta name="robots" content="noindex, follow" />}
        <link rel="canonical" href="https://cryptobag.net/prices" />
      </Helmet>

      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8 space-y-6">
        <GlobalMarketTicker />
        
        {/* SEO HEADER: This tells Google this is the primary content hub */}
        <header className="mb-2">
          <h1 className="text-4xl font-black text-white tracking-tighter mb-8 leading-tight">
            Live Crypto Prices <span className="text-indigo-500">&amp; Market Movers</span>
          </h1>
          <p className="mt-2 text-gray-400 text-lg">
            Up-to-the-minute data, market sentiment, and trending assets.
          </p>
        </header>

        {/* Top Dashboard */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            <MarketPulse coins={coinsData} loading={coinsLoading} />
          </div>
          <div className="lg:col-span-1">
            <FearGreedGauge
              loading={sentimentLoading}
              value={fearGreed?.value}
              classification={fearGreed?.classification}
            />
            {sentimentError && (
              <p className="text-red-500 text-xs mt-2 text-center">Market Sentiment data failed to load.</p>
            )}
          </div>
        </div>

        {/* Trending Now */}
        <TrendingNow loading={trendingLoading} trendingCoins={trendingCoins} onCoinClick={handleCoinClick} />
        {trendingError && (
          <p className="text-red-500 text-xs text-center -mt-4 mb-4">Trending data failed to load.</p>
        )}

        {/* Search Bar */}
        <div className="relative mb-6">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {icons.search({ className: 'w-5 h-5 text-gray-400' })}
          </div>
          <input
            type="text"
            placeholder="Search by coin or symbol..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-3 bg-gray-800/60 border border-gray-700/50 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 backdrop-blur-sm"
          />
        </div>

        {/* Loading State */}
        {coinsLoading && <CryptoTableSkeleton />}

        {/* Error State */}
        {coinsError && !coinsLoading && (
          <div className="bg-red-900/50 border border-red-700 text-red-100 px-4 py-3 rounded-lg text-center">
            <h3 className="font-bold">Error</h3>
            <p>{coinsError}</p>
          </div>
        )}

        {/* Main Table */}
        {!coinsLoading && !coinsError && filteredCoins.length > 0 && (
          <CryptoTable coins={filteredCoins} onCoinClick={handleCoinClick} />
        )}

        {/* No Results */}
        {!coinsLoading && !coinsError && filteredCoins.length === 0 && searchTerm && (
          <div className="bg-gray-800/60 backdrop-blur-sm border border-gray-700/50 p-12 rounded-lg shadow-2xl text-center">
            <h3 className="text-2xl text-white mb-2">No Results Found</h3>
            <p className="text-gray-400">No tokens matched your search for "{searchTerm}".</p>
          </div>
        )}
      </div>
    </>
  );
};

export default Prices;
